import { useCallback, useEffect, useRef, useState } from 'react';

import { skipToken, useQuery } from '@tanstack/react-query';

import useDebouncedValue from './useDebounceValue';

import type { UseQueryOptions } from '@tanstack/react-query';

interface UseAsyncValidationOptions<
  TParams,
  TResult,
  TError = Error,
  TQueryKey extends readonly unknown[] = readonly unknown[],
> {
  /** result phase: API 응답(TResult)으로부터 에러 메시지를 결정 */
  getResultMessage: (result: TResult) => string | null;

  queryOptions: (params: TParams) => UseQueryOptions<TResult, TError, TResult, TQueryKey>;
  debounceMs?: number;
  /**
   * error phase: API 에러(TError)로부터 에러 메시지를 결정.
   * 미제공 시 또는 null/empty 반환 시 fallback '검증에 실패했습니다.' 적용.
   */
  getErrorMessage?: (error: TError) => string | null;
  onValidationComplete?: (result: { isValid: boolean; message: string | null }) => void;
}

interface UseAsyncValidationResult<TParams> {
  clearError: () => void;
  errorMessage: string | null;
  hasError: boolean;
  isPending: boolean;
  isValidating: boolean;
  resetPrevError: () => void;
  validateAsync: (currentParams?: TParams) => Promise<string | null>;
}

export default function useAsyncValidation<
  TParams,
  TResult,
  TError = Error,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  params: TParams,
  options: UseAsyncValidationOptions<TParams, TResult, TError, TQueryKey>,
): UseAsyncValidationResult<TParams> {
  const {
    queryOptions: createQueryOptions,
    getResultMessage,
    getErrorMessage,
    debounceMs = 300,
    onValidationComplete,
  } = options;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedParams = useDebouncedValue(params, debounceMs);

  const isPending = params !== debouncedParams;

  const queryOpts = createQueryOptions(debouncedParams);

  const isEnabled = queryOpts.enabled !== false;

  const query = useQuery({
    ...queryOpts,
    enabled: isEnabled && !isPending,
  });

  // undefined: 아직 검증 안됨, null: 검증 성공, string: 검증 실패
  const prevErrorMessageRef = useRef<string | null | undefined>(undefined);

  // onValidationComplete 참조 변경 시 prevErrorMessageRef 리셋
  // → 외부에서 에러가 클리어된 후(resolver 등) 동일 메시지에 대해 onValidationComplete를 재호출할 수 있도록 허용
  const prevOnValidationCompleteRef = useRef(onValidationComplete);
  useEffect(() => {
    if (prevOnValidationCompleteRef.current !== onValidationComplete) {
      prevErrorMessageRef.current = undefined;
    }
    prevOnValidationCompleteRef.current = onValidationComplete;
  }, [onValidationComplete]);

  const resolveResultMessage = useCallback(
    (result: TResult): string | null => {
      const message = getResultMessage(result);
      return typeof message === 'string' && message.trim().length > 0 ? message : null;
    },
    [getResultMessage],
  );

  const resolveErrorMessage = useCallback(
    (error: TError): string => {
      let message: string | null = null;

      try {
        message = getErrorMessage?.(error) ?? null;
      } catch {
        // getErrorMessage 자체 예외 시 fallback으로 수렴
      }

      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }

      return '검증에 실패했습니다.';
    },
    [getErrorMessage],
  );

  useEffect(() => {
    if (!isEnabled || !query.isSuccess || query.isFetching) {
      return;
    }

    const message = resolveResultMessage(query.data as TResult);

    if (message !== prevErrorMessageRef.current) {
      prevErrorMessageRef.current = message;
      setErrorMessage(message);
      onValidationComplete?.({ isValid: message === null, message });
    }
  }, [
    isEnabled,
    query.isSuccess,
    query.isFetching,
    query.data,
    onValidationComplete,
    resolveResultMessage,
  ]);

  // error phase: API 에러(query.isError) 시 getErrorMessage를 통해 에러 메시지 결정
  useEffect(() => {
    if (!isEnabled || !query.isError || query.isFetching) {
      return;
    }

    // AbortError는 validateAsync와 동일하게 무시
    if (query.error instanceof Error && query.error.name === 'AbortError') {
      return;
    }

    const message = resolveErrorMessage(query.error as TError);

    if (message !== prevErrorMessageRef.current) {
      prevErrorMessageRef.current = message;
      setErrorMessage(message);
      onValidationComplete?.({ isValid: false, message });
    }
  }, [
    isEnabled,
    query.isError,
    query.isFetching,
    query.error,
    onValidationComplete,
    resolveErrorMessage,
  ]);

  useEffect(() => {
    if (!isEnabled) {
      setErrorMessage(null);
    }
  }, [isEnabled]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage(null);
    prevErrorMessageRef.current = undefined;
  }, []);

  // errorMessage 상태는 유지한 채 prevErrorMessageRef만 리셋하여,
  // 다음 effect cycle에서 onValidationComplete를 재호출하도록 허용.
  // clearError()와 달리 UI 번쩍임이 발생하지 않음.
  const resetPrevError = useCallback(() => {
    prevErrorMessageRef.current = undefined;
  }, []);

  const validateAsync = useCallback(
    async (currentParams?: TParams): Promise<string | null> => {
      const targetParams = currentParams ?? paramsRef.current;
      const opts = createQueryOptions(targetParams);

      if (opts.enabled === false) {
        return null;
      }

      const { queryFn, queryKey } = opts;

      if (!queryFn || queryFn === skipToken || !queryKey) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[useAsyncValidation] queryOptions must include queryFn and queryKey.', {
            queryFn: !!queryFn,
            queryKey: !!queryKey,
          });
        }
        return null;
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      try {
        const result = await (
          queryFn as (context: {
            meta: Record<string, unknown> | undefined;
            queryKey: readonly unknown[];
            signal: AbortSignal;
          }) => Promise<TResult>
        )({
          signal: abortControllerRef.current.signal,
          queryKey,
          meta: undefined,
        });
        const message = resolveResultMessage(result);

        setErrorMessage(message);
        onValidationComplete?.({ isValid: message === null, message });
        return message;
      } catch (error) {
        // AbortError 계약은 유지: 사용자 입력 전환으로 취소된 요청은 검증 실패로 취급하지 않는다.
        if (error instanceof Error && error.name === 'AbortError') {
          return null;
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('[useAsyncValidation] Validation failed:', error);
        }

        let errorMessage: string | null = null;

        try {
          errorMessage = getErrorMessage?.(error as TError) ?? null;
        } catch {
          // getErrorMessage 자체 예외 시 fallback으로 수렴
        }

        const resolvedMessage =
          typeof errorMessage === 'string' && errorMessage.trim().length > 0
            ? errorMessage
            : '검증에 실패했습니다.';

        setErrorMessage(resolvedMessage);
        onValidationComplete?.({ isValid: false, message: resolvedMessage });
        return resolvedMessage;
      }
    },
    [
      createQueryOptions,
      getResultMessage,
      getErrorMessage,
      onValidationComplete,
      resolveResultMessage,
    ],
  );

  return {
    errorMessage,
    hasError: errorMessage !== null,
    isValidating: query.isFetching,
    isPending,
    validateAsync,
    clearError,
    resetPrevError,
  };
}

export type { UseAsyncValidationOptions, UseAsyncValidationResult };
